/**
 * Which deployment is this?
 *
 * Isomorphic and env-var-free on purpose. `NODE_ENV` is "production" in every
 * built deploy (prod, dev, previews), so it can't tell them apart, and
 * NEXT_PUBLIC_* vars are inlined at BUILD time — a dev service that forgets one
 * silently behaves like prod. Both failure modes are how dev traffic ends up in
 * production analytics.
 *
 * So we key off the host actually serving the request. A deploy is only
 * "production" if it's being served from the real domain. Everything else —
 * Railway dev, preview deploys, localhost — is non-production automatically,
 * with nothing to configure and nothing to forget.
 */

/** The only hosts that count as production. */
export const PRODUCTION_HOSTS = [
  "areaadvertising.com",
  "www.areaadvertising.com",
];

/** True only for the live public site. Pass a hostname or a Host header
 *  (a `:port` suffix is tolerated). Anything falsy → not production. */
export function isProductionHost(host?: string | null): boolean {
  if (!host) return false;
  const hostname = host.split(":")[0].trim().toLowerCase();
  return PRODUCTION_HOSTS.includes(hostname);
}

/** Client-side convenience: is the page currently on the production domain?
 *  False during SSR (no window), which is the safe default — analytics is
 *  started from the browser anyway. */
export function isProductionClient(): boolean {
  if (typeof window === "undefined") return false;
  return isProductionHost(window.location.hostname);
}

/**
 * The PUBLIC origin a request arrived on (e.g. https://dev-….up.railway.app),
 * for building absolute URLs we email or redirect to.
 *
 * Behind a proxy like Railway, the server binds an internal address
 * (localhost:8080) and `req.nextUrl.origin` reflects THAT, not the domain the
 * user's browser hit. The proxy records the real one in `x-forwarded-host` /
 * `x-forwarded-proto`, so we prefer those. `req` is anything exposing a
 * `headers.get()` (a NextRequest or a Fetch Request).
 */
export function publicOrigin(req: {
  headers: { get(name: string): string | null };
  nextUrl?: { origin: string };
}): string {
  const fwdHost = req.headers.get("x-forwarded-host") ?? req.headers.get("host");
  if (fwdHost) {
    // A forwarded header can carry a comma-separated chain; the first is the
    // originating client-facing host.
    const host = fwdHost.split(",")[0].trim();
    const proto =
      req.headers.get("x-forwarded-proto")?.split(",")[0].trim() ||
      (host.startsWith("localhost") || host.startsWith("127.0.0.1") ? "http" : "https");
    return `${proto}://${host}`;
  }
  return req.nextUrl?.origin ?? "http://localhost:3000";
}
